import React, { useState } from 'react';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { useWaitlist } from '../hooks/useWaitlist';
import { trackEvent, trackCTAClick } from '../lib/analytics';

interface WaitlistFormProps {
  buttonText?: string;
  onSuccess?: () => void;
  location?: string;
}

export function WaitlistForm({ 
  buttonText = "Gå med i väntelistan", 
  onSuccess,
  location = 'unknown'
}: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);
  const { submitEmail, isSubmitting, error } = useWaitlist();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Always trim email input
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setShowError(true);
      return;
    }

    setShowError(false);
    trackCTAClick(buttonText, location);
    
    const success = await submitEmail(trimmedEmail);
    if (success) {
      trackEvent('waitlist_submit', { 
        email: trimmedEmail,
        location,
        buttonText
      });
      setEmail('');
      onSuccess?.();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col gap-3">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3 animate-fadeIn">
            <AlertCircle className="text-red-500 shrink-0" size={20} />
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}
        
        <div className="relative">
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setShowError(false);
            }}
            placeholder="Din e-post för intresseanmälan"
            className={`w-full px-4 py-4 text-lg border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm placeholder:text-gray-400 ${
              showError ? 'border-red-300' : 'border-gray-200'
            }`}
            required
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
            title="Ange en giltig e-postadress"
          />
          {showError && (
            <p className="absolute -bottom-6 left-0 text-sm text-red-600">
              Ange en giltig e-postadress
            </p>
          )}
        </div>
        <button 
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gray-900 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-800 active:bg-gray-950 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl disabled:opacity-50"
        >
          <span>{isSubmitting ? 'Registrerar...' : buttonText}</span>
          <ArrowRight size={20} />
        </button>
      </div>
      <p className="text-sm text-gray-500 text-center">
        Genom att ange din e-post visar du intresse för projektet. Ingen förpliktelse.
      </p>
    </form>
  );
}